import { useEffect, useState } from 'react'
import type { JSX } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import type { ErrorLike } from '@apollo/client'

import { Button, Flex, Skeleton, Table } from '@buffer-mono/popcorn'

import { normalizeNetworkName } from '~publish/helpers/channels'

import { ChartErrorState } from '../../components/ChartErrorState'
import { NoDataEmptyState } from '../../components/NoDataEmptyState'
import { SectionCard } from '../../components/SectionCard'
import { formatInsightsDateRange, getNoDataDescription } from '../../utils'
import type { ChannelInsight } from '../hooks/useAllChannelsInsightsSummary'

import { CHANNELS_PER_PAGE, PerformanceTableView } from './PerformanceTableView'
import styles from './AllChannelsPerformanceTable.module.css'

const SKELETON_COLUMN_KEYS = ['metric-1', 'metric-2', 'metric-3']

/**
 * Placeholder shown while insights load. Mirrors the real table's header plus a
 * full page of body rows using the same row classes, so the section keeps a
 * stable height and doesn't jump when data arrives.
 */
export function PerformanceTableSkeleton({
  rows = CHANNELS_PER_PAGE,
}: {
  rows?: number
}): JSX.Element {
  const rowKeys = Array.from(
    { length: rows },
    (_, index) => `skeleton-row-${index}`,
  )
  return (
    <Table>
      <Table.Header className={styles.tableHeader}>
        <Table.Row className={styles.tableHeaderRow}>
          <Table.HeaderCell align="left">Channel</Table.HeaderCell>
          {SKELETON_COLUMN_KEYS.map((columnKey) => (
            <Table.HeaderCell key={columnKey} align="left">
              <Skeleton width={56} height={14} />
            </Table.HeaderCell>
          ))}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {rowKeys.map((rowKey) => (
          <Table.Row key={rowKey} className={styles.channelRow}>
            <Table.Cell className={styles.postCell}>
              <Flex gap="sm" align="center">
                <Skeleton width={32} height={32} />
                <Skeleton
                  width={120}
                  height={14}
                  className={styles.channelName}
                />
              </Flex>
            </Table.Cell>
            {SKELETON_COLUMN_KEYS.map((columnKey) => (
              <Table.Cell
                key={columnKey}
                align="left"
                className={styles.postCell}
              >
                <Skeleton width={40} height={12} />
              </Table.Cell>
            ))}
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  )
}

interface AllChannelsPerformanceTableProps {
  channelInsights: ChannelInsight[]
  loading: boolean
  refreshing?: boolean
  error?: ErrorLike
  startDate: Date
  endDate: Date
  /** When set, renders the section subtitle as "From … · Compared to …". */
  comparisonStartDate?: Date | null
  comparisonEndDate?: Date | null
  selectedDateRange?: string
  hasChannelFilter?: boolean
  onClearChannelFilter?: () => void
  /**
   * Active sort (metric `key` + direction), owned by the page so the export
   * carries the same order the user sees. `key` is a `PostMetricType` value.
   */
  sortConfig: { key: string; direction: 'asc' | 'desc' }
  /** Toggles/sets the sort for the clicked metric column. */
  onSort: (metricType: string) => void
}

export function AllChannelsPerformanceTable({
  channelInsights,
  loading,
  refreshing = false,
  error,
  startDate,
  endDate,
  comparisonStartDate,
  comparisonEndDate,
  selectedDateRange,
  hasChannelFilter,
  onClearChannelFilter,
  sortConfig,
  onSort,
}: AllChannelsPerformanceTableProps): JSX.Element {
  const { search } = useLocation()
  const navigate = useNavigate()

  const [page, setPage] = useState(1)

  // Reset to the first page when the date range changes: the parent feeds a
  // fresh dataset and a stale page could otherwise land on an empty page.
  useEffect(() => {
    setPage(1)
  }, [startDate, endDate])

  // Reset to the first page when the sort changes — the row order is fully
  // reshuffled, so a stale page would otherwise show an arbitrary slice. The
  // synchronous reset lives in `handleSort` below so a header click from page
  // N never paints page N of the newly-sorted list for a frame; this effect is
  // the safety net for externally-driven sort changes (sort is parent-owned).
  useEffect(() => {
    setPage(1)
  }, [sortConfig.key, sortConfig.direction])

  // Reset the page synchronously on a header click, before delegating the sort
  // to the parent, to avoid the one-frame flash described above.
  const handleSort = (metricType: string): void => {
    setPage(1)
    onSort(metricType)
  }

  const content = loading ? (
    <PerformanceTableSkeleton />
  ) : error ? (
    <div className={styles.emptyState}>
      <ChartErrorState />
    </div>
  ) : channelInsights.length > 0 ? (
    <PerformanceTableView
      channelInsights={channelInsights}
      scopeId="all-channels"
      sortConfig={sortConfig}
      onSort={handleSort}
      page={page}
      onPageChange={setPage}
      refreshing={refreshing}
      renderRowAction={(insight) => (
        <Button
          variant="secondary"
          size="small"
          className={styles.viewMoreButton}
          aria-label={`View More for ${normalizeNetworkName(
            insight.channel.service,
          )} ${insight.channel.name}`}
          onClick={() => navigate(`/insights/${insight.channel.id}${search}`)}
        >
          View More
        </Button>
      )}
    />
  ) : (
    <NoDataEmptyState
      description={getNoDataDescription(selectedDateRange, hasChannelFilter)}
      showSuggestion
      onClearChannelFilter={onClearChannelFilter}
    />
  )

  return (
    <SectionCard>
      <SectionCard.Header>
        <SectionCard.Title>Performance</SectionCard.Title>
        <SectionCard.Subtitle>
          {formatInsightsDateRange(
            startDate,
            endDate,
            comparisonStartDate,
            comparisonEndDate,
          )}
        </SectionCard.Subtitle>
      </SectionCard.Header>
      {content}
    </SectionCard>
  )
}
